import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKeyhole } from "lucide-react";

export default function Login({ redirectTo = "/admin" }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="login-page">
      <Seo title="Sign In" description="Sign in to Beyond Board Woodworking." path="/login" noindex />
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-stone-100/10 bg-zinc-900/60 p-10 space-y-6" data-testid="login-form">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 rounded-md bg-[#D8A34B]/15 border border-[#D8A34B]/40 flex items-center justify-center">
            <LockKeyhole className="w-5 h-5 text-[#D8A34B]" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D8A34B]">Beyond Board Woodwork</p>
            <h1 className="font-display text-2xl text-[#F5F0E6]">Beyond Board Admin</h1>
          </div>
        </div>
        <p className="text-sm text-stone-400 -mt-2">
          Sign in to manage events and website updates.
        </p>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-stone-200">Email</Label>
          <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-950/60 border-stone-100/10 text-[#F5F0E6] focus-visible:ring-[#D8A34B]" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-stone-200">Password</Label>
          <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-zinc-950/60 border-stone-100/10 text-[#F5F0E6] focus-visible:ring-[#D8A34B]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <Button data-testid="login-submit" disabled={busy} className="w-full bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C] h-12">
            {busy ? "Signing in…" : "Log In"}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="login-guest"
            onClick={() => navigate("/")}
            className="w-full h-12 bg-transparent border-stone-100/20 text-stone-200 hover:bg-stone-100/5"
          >
            Continue as Guest
          </Button>
        </div>
      </form>
    </div>
  );
}
